'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  AIBotConfig,
  AIBotSession,
  BotMessage,
  BotInfo,
  SessionType,
  RegisterBotSessionRequest,
  SendBotMessageRequest,
  BotSessionEstablishedResponse,
  BotSwitchedNotification,
} from '@/types/ai-bot-framework';
import { useAIBotProvider } from '@/contexts/ai-bot-context';
import { getCurrentPageContext } from '@/utils/page-context-parser';

/**
 * Core hook for AI Bot Framework - provides explicit session management and communication.
 *
 * Uses self-describing BotMessage fields:
 * - message.status  → status update (don't add to messages)
 * - message.error   → error handling
 * - message.content → text content (append/create)
 * - message.interactiveElements → pass through to messages array
 * - message.isComplete → stop processing
 */
export function useAIBot(config: AIBotConfig): AIBotSession {
  // Generate stable session ID
  const sessionId = useRef(generateSessionId()).current;

  // State management
  const [isRegistered, setIsRegistered] = useState(false);
  const [currentBot, setCurrentBot] = useState<BotInfo | null>(null);
  const [availableBots, setAvailableBots] = useState<BotInfo[]>([]);
  const [messages, setMessages] = useState<BotMessage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const processingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Get SignalR connection from provider
  const { connection, isConnected, registerMessageHandler, unregisterMessageHandler } = useAIBotProvider();

  // Refs for cleanup and state management
  const registrationPromiseRef = useRef<Promise<void> | null>(null);
  const isUnmountingRef = useRef(false);
  const configRef = useRef(config);
  const initialMessageSentRef = useRef(false);

  // Ensure unmounting flag starts as false
  useEffect(() => {
    isUnmountingRef.current = false;
  }, []);

  // Update config ref when config changes
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Generate unique session ID
  function generateSessionId(): string {
    return `${config.sessionType.toLowerCase()}-${Date.now()}-${uuidv4().substring(0, 8)}`;
  }

  // ---- Message handler (self-describing field checks) ----
  const handleBotMessage = useCallback((message: BotMessage) => {
    if (message.sessionId !== sessionId) {
      return; // Session isolation
    }

    // Call custom message handler if provided
    if (configRef.current.onMessage) {
      try {
        configRef.current.onMessage(message);
      } catch (err) {
        console.error('Error in custom message handler:', err);
      }
    }

    // Status-only message → don't add to messages array
    if (message.status && !message.content && !message.interactiveElements && !message.visualization && !message.error) {
      // Keep processing spinner alive
      setIsProcessing(true);

      // Handle completion via isComplete on status messages
      if (message.isComplete) {
        setIsProcessing(false);
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
          processingTimeoutRef.current = undefined;
        }
      }
      return;
    }

    // Error handling
    if (message.isError || message.error) {
      const errorMsg = message.error?.message || 'An error occurred';
      setError(errorMsg);
      setIsProcessing(false);

      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = undefined;
      }
      return;
    }

    // For visible chats, update messages state
    if (configRef.current.sessionType === SessionType.Visible) {
      setMessages(prev => {
        // Find existing message or create new one
        const existingIndex = prev.findIndex(m => m.requestId === message.requestId);

        if (existingIndex >= 0) {
          // Update existing message — append content
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            content: (updated[existingIndex].content || '') + (message.content || ''),
            isComplete: message.isComplete,
            timestamp: message.timestamp,
            interactiveElements: message.interactiveElements || updated[existingIndex].interactiveElements,
            visualization: message.visualization || updated[existingIndex].visualization,
          };
          return updated;
        } else if (message.content || message.interactiveElements || message.visualization) {
          // Add new message (has renderable content)
          return [...prev, message];
        }

        return prev;
      });
    }

    // Update processing state
    if (message.isComplete) {
      setIsProcessing(false);
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = undefined;
      }
    }
  }, [sessionId]);

  // ---- Element response handler (action progress/results) ----
  const handleElementResponse = useCallback((message: BotMessage) => {
    if (message.sessionId !== sessionId) {
      return;
    }

    // Call custom message handler for element responses too
    if (configRef.current.onMessage) {
      try {
        configRef.current.onMessage(message);
      } catch (err) {
        console.error('Error in custom message handler (element response):', err);
      }
    }

    // Add element response content to messages
    if (configRef.current.sessionType === SessionType.Visible && message.content) {
      setMessages(prev => [...prev, message]);
    }
  }, [sessionId]);


  const handleSessionEstablished = useCallback((response: BotSessionEstablishedResponse) => {
    if (response.sessionId !== sessionId) {
      return;
    }

    setCurrentBot(response.assignedBot);
    setIsRegistered(true);
    setError(null);

    if (!response.requestedBotAvailable && response.fallbackReason) {
      console.warn('Requested bot not available:', response.fallbackReason);
    }
  }, [sessionId]);

  const handleBotSwitched = useCallback((notification: BotSwitchedNotification) => {
    if (notification.sessionId !== sessionId) return;

    setCurrentBot(notification.newBot);

    // Add system message about bot switch for visible chats
    if (configRef.current.sessionType === SessionType.Visible) {
      const switchMessage: BotMessage = {
        sessionId,
        requestId: `switch-${Date.now()}`,
        botId: 'system',
        botName: 'System',
        content: `Bot switched to ${notification.newBot.botName}: ${notification.reason}`,
        isComplete: true,
        isError: false,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, switchMessage]);
    }
  }, [sessionId]);

  const handleSessionTerminated = useCallback((terminatedSessionId: string, reason: string) => {
    if (terminatedSessionId !== sessionId) return;

    setError(`Session terminated: ${reason}`);
    setIsRegistered(false);
    setCurrentBot(null);
    setIsProcessing(false);
  }, [sessionId]);

  const handleAvailableBotsUpdated = useCallback((data: { sessionId: string; availableBots: BotInfo[] }) => {
    if (data.sessionId !== sessionId) return;

    setAvailableBots(data.availableBots);
  }, [sessionId]);

  // Track if handlers are registered
  const [handlersRegistered, setHandlersRegistered] = useState(false);

  // Register message handlers with connection
  useEffect(() => {
    if (!connection || !isConnected) {
      setHandlersRegistered(false);
      return;
    }

    const handlers = {
      ReceiveBotMessage: handleBotMessage,
      ReceiveElementResponse: handleElementResponse,
      BotSessionEstablished: handleSessionEstablished,
      BotSwitched: handleBotSwitched,
      SessionTerminated: handleSessionTerminated,
      AvailableBotsUpdated: handleAvailableBotsUpdated
    };

    // Register handlers
    Object.entries(handlers).forEach(([eventName, handler]) => {
      registerMessageHandler(eventName, handler);
    });

    setHandlersRegistered(true);

    return () => {
      Object.keys(handlers).forEach(eventName => {
        unregisterMessageHandler(eventName);
      });
      setHandlersRegistered(false);
    };
  }, [
    connection,
    isConnected,
    handleBotMessage,
    handleElementResponse,
    handleSessionEstablished,
    handleBotSwitched,
    handleSessionTerminated,
    handleAvailableBotsUpdated,
    registerMessageHandler,
    unregisterMessageHandler,
    sessionId
  ]);

  // Register session when connected
  const registerSession = useCallback(async (): Promise<void> => {
    if (!connection || !isConnected || !handlersRegistered || isRegistered || registrationPromiseRef.current) {
      return registrationPromiseRef.current || Promise.resolve();
    }

    const registrationPromise = (async () => {
      try {
        const pageContext = getCurrentPageContext();

        const request: RegisterBotSessionRequest = {
          sessionId,
          requestedBotId: config.requestedBotId,
          sessionType: config.sessionType,
          pageContext
        };

        await connection.invoke('RegisterBotSession', request);

        // Wait a bit to see if we get the response
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (err) {
        console.error('Failed to register bot session:', err);
        setError('Failed to register bot session');
        throw err;
      } finally {
        registrationPromiseRef.current = null;
      }
    })();

    registrationPromiseRef.current = registrationPromise;
    return registrationPromise;
  }, [connection, isConnected, isRegistered, sessionId, config, handlersRegistered]);

  // Auto-register when connected and handlers are ready
  useEffect(() => {
    const shouldRegister = isConnected && handlersRegistered && !isRegistered && !isUnmountingRef.current;

    if (shouldRegister) {
      registerSession().catch(err => {
        console.error('Auto-registration failed:', err);
      });
    }
  }, [isConnected, handlersRegistered, isRegistered, registerSession]);

  // Send message function
  const sendMessage = useCallback(async (content: string, metadata?: Record<string, any>): Promise<void> => {
    if (!connection || !isConnected || !isRegistered) {
      throw new Error('Not connected or registered');
    }

    if (!content.trim()) {
      throw new Error('Message content cannot be empty');
    }

    const requestId = `${sessionId}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

    // Add user message to visible chats
    if (config.sessionType === SessionType.Visible) {
      const userMessage: BotMessage = {
        sessionId,
        requestId: `user-${requestId}`,
        botId: 'user',
        botName: 'You',
        content,
        isComplete: true,
        isError: false,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMessage]);
    }

    setIsProcessing(true);
    setError(null);

    // Clear any existing timeout
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
    }

    // Set a timeout to stop processing after 30 seconds as a fallback
    processingTimeoutRef.current = setTimeout(() => {
      console.warn('Processing timeout reached, stopping spinner');
      setIsProcessing(false);
    }, 30000);

    try {
      const pageContext = getCurrentPageContext();

      const request: SendBotMessageRequest = {
        sessionId,
        content,
        requestId,
        metadata: metadata || {},
        pageContext
      };

      await connection.invoke('SendBotMessage', request);
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message');
      setIsProcessing(false);
      throw err;
    }
  }, [connection, isConnected, isRegistered, sessionId, config.sessionType]);

  // Respond to an interactive element
  const respondToElement = useCallback(async (elementId: string, actionMetadata: Record<string, unknown>): Promise<void> => {
    if (!connection || !isConnected || !isRegistered) {
      throw new Error('Not connected or registered');
    }

    try {
      await connection.invoke('RespondToElement', {
        sessionId,
        elementId,
        actionMetadata,
      });
    } catch (err) {
      console.error('Failed to respond to element:', err);
      throw err;
    }
  }, [connection, isConnected, isRegistered, sessionId]);

  // Switch to a different bot
  const switchBot = useCallback(async (newBotId: string): Promise<void> => {
    if (!connection || !isConnected || !isRegistered) {
      throw new Error('Not connected or registered');
    }

    if (!newBotId) {
      throw new Error('Bot ID is required');
    }

    try {
      await connection.invoke('SwitchBotInSession', sessionId, newBotId);
    } catch (err) {
      console.error('Failed to switch bot:', err);
      setError('Failed to switch bot');
      throw err;
    }
  }, [connection, isConnected, isRegistered, sessionId]);

  // Unregister session
  const unregisterSession = useCallback(async (): Promise<void> => {
    if (!connection || !sessionId || !isRegistered) return;

    try {
      if (isConnected) {
        await connection.invoke('UnregisterBotSession', sessionId);
      }
      setIsRegistered(false);
      setCurrentBot(null);
      setIsProcessing(false);
    } catch (err) {
      console.error('Failed to unregister session:', err);
    }
  }, [connection, isConnected, sessionId, isRegistered]);

  // Clear messages (for visible chats)
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  // Reconnect function
  const reconnect = useCallback(async (): Promise<void> => {
    setError(null);
    setIsRegistered(false);
    setCurrentBot(null);
    setIsProcessing(false);

    if (isConnected) {
      await registerSession();
    }
  }, [isConnected, registerSession]);

  // Auto-send initial message after registration
  useEffect(() => {
    if (!isRegistered || !config.initialMessage || initialMessageSentRef.current || isProcessing) {
      return;
    }

    let cancelled = false;

    const timer = setTimeout(() => {
      if (cancelled) return;
      if (initialMessageSentRef.current) return;

      initialMessageSentRef.current = true;

      sendMessage(config.initialMessage!, { isInitialMessage: true })
        .catch(err => {
          console.error('Failed to send initial message:', err);
          if (!cancelled) {
            initialMessageSentRef.current = false;
          }
        });
    }, 200);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isRegistered, config.initialMessage, sendMessage, isProcessing]);

  // Cleanup on unmount
  useEffect(() => {
    isUnmountingRef.current = false;
    initialMessageSentRef.current = false;

    return () => {
      isUnmountingRef.current = true;
      initialMessageSentRef.current = false;
      unregisterSession().catch(err => {
        console.error('Cleanup unregister failed:', err);
      });
    };
  }, [unregisterSession]);

  return {
    sessionId,
    isConnected,
    isRegistered,
    currentBot,
    availableBots,
    messages,
    isProcessing,
    error,
    sendMessage,
    respondToElement,
    switchBot,
    registerSession,
    unregisterSession,
    clearMessages,
    reconnect
  };
}
